import React from 'react';
import { MapPin } from 'lucide-react';
import { Card } from '@mts/ui';

function buildMapQuery(lat, lng, address, name) {
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);

  if (hasCoordinates) {
    return `${lat},${lng}`;
  }

  const addressText = String(address || '').trim();
  if (addressText) {
    return addressText;
  }

  const nameText = String(name || '').trim();
  return nameText;
}

function BasicBusinessMapCard({ lat, lng, address, name, mapField, isBasicTier = false }) {
  const query = buildMapQuery(lat, lng, address, name);

  if (!query) {
    return (
      <div id="BusinessMapCard">
        <Card
          id="BusinessMapCard-Card"
          className="my-[10px] rounded-lg"
          style={{
            backgroundColor: 'var(--card)',
            borderColor: 'var(--border)',
            borderWidth: '1px',
            borderStyle: 'solid',
          }}
        >
        <Card.Header id="BusinessMapCard-CardHeader" className="flex items-start justify-between gap-3">
          <div id="BusinessMapCard-CardHeaderInner">
            <h2 className="text-base font-semibold text-[var(--primary)]">Business Map</h2>
            <Card.Title id="BusinessMapCard-CardTitle">
              <span className="sr-only">Business location map</span>
            </Card.Title>
            <Card.Description id="BusinessMapCard-CardDescription">
              <span className="sr-only">Map preview</span>
            </Card.Description>
          </div>
          <Card.Action id="BusinessMapCard-CardAction">
            <div />
          </Card.Action>
        </Card.Header>

        <Card.Content id="BusinessMapCard-CardContent" className="before:content-none after:content-none">
          <p className="text-sm text-black/70">Map preview unavailable for this listing.</p>
        </Card.Content>

        <Card.Footer id="BusinessMapCard-CardFooter">
          <div />
        </Card.Footer>
        </Card>
      </div>
    );
  }

  const embedUrl = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=15&output=embed`;
  const directionsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  return (
    <div id="BusinessMapCard">
      <Card
        id="BusinessMapCard-Card"
        className="my-[10px] rounded-lg"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
      <Card.Header id="BusinessMapCard-CardHeader" className="flex items-start justify-between gap-3">
        <div id="BusinessMapCard-CardHeaderInner">
          <h2 className="text-base font-semibold text-[var(--primary)]">Business Map</h2>
          <Card.Title id="BusinessMapCard-CardTitle">
            <span className="sr-only">Business location map</span>
          </Card.Title>
          <Card.Description id="BusinessMapCard-CardDescription">
            <span className="sr-only">Map preview</span>
          </Card.Description>
        </div>
        <Card.Action id="BusinessMapCard-CardAction">
          <div />
        </Card.Action>
      </Card.Header>

      <Card.Content id="BusinessMapCard-CardContent" className="before:content-none after:content-none">
        <div title={`listing field: ${mapField}`}>
        <div
          className="relative overflow-hidden rounded-lg border"
          style={{ borderColor: 'var(--ui-input-border)' }}
        >
          <iframe
            title={`Map for ${name || 'business location'}`}
            src={embedUrl}
            className="h-56 w-full border-0"
            style={{ filter: isBasicTier ? 'grayscale(1) contrast(1.05)' : undefined }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />

          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            className="absolute bottom-3 right-3 inline-flex h-9 items-center gap-2 rounded-lg border border-black/20 bg-white px-4 text-sm font-medium text-black shadow-sm transition-colors hover:border-black hover:bg-black hover:text-white"
            style={{
              borderColor: 'var(--ui-input-border)',
            }}
          >
            <MapPin size={14} />
            Directions
          </a>
        </div>
        </div>
      </Card.Content>

      <Card.Footer id="BusinessMapCard-CardFooter">
        <div />
      </Card.Footer>
      </Card>
    </div>
  );
}

export default BasicBusinessMapCard;
